<script setup lang="ts">
const route = useRoute()
const slug = route.params.slug as string

const { data: article, status } = await useArticle(slug)
const { data: allArticles } = await useBlogList()

const moreStories = computed(() =>
  (allArticles.value as any[])
    .filter((a: any) => a.handle !== slug)
    .slice(0, 4),
)

const byline = computed(() => {
  if (!article.value) return ''
  return `By ${article.value.author_name}  ·  ${article.value.formattedDate}  ·  ${article.value.readTime}`
})
</script>

<template>
  <div class="bg-page flex flex-col items-center w-full">
    <!-- Loading state -->
    <div
      v-if="status === 'pending'"
      class="flex items-center justify-center w-full h-screen"
    >
      <p class="font-label font-light text-[13px] text-brand tracking-[2px]">
        Loading...
      </p>
    </div>

    <!-- 404 state -->
    <div
      v-else-if="!article"
      class="flex items-center justify-center w-full h-screen"
    >
      <p class="font-label font-light text-[13px] text-muted tracking-[2px]">
        Article not found.
      </p>
    </div>

    <template v-else>
      <!-- HERO -->
      <div
        class="bg-dark relative h-[420px] md:h-[560px] w-full overflow-hidden shrink-0"
      >
        <video
          v-if="article.hero_video"
          autoplay
          loop
          muted
          playsinline
          preload="metadata"
          :poster="article.image"
          class="absolute inset-0 w-full h-full object-cover pointer-events-none"
        >
          <source :src="article.hero_video" type="video/mp4" />
        </video>
        <img
          v-else
          :src="article.image"
          :alt="article.title"
          class="absolute inset-0 w-full h-full object-cover pointer-events-none"
        />
        <div class="absolute inset-0 bg-black/50 pointer-events-none z-10" />
      </div>

      <!-- CONTENT WRAPPER -->
      <div class="flex flex-col items-center w-full md:w-4/6 mx-auto">
        <!-- ARTICLE HEADER -->
        <div
          class="bg-page flex flex-col items-center justify-center overflow-hidden py-6 w-full"
        >
          <div
            class="flex flex-col gap-4 items-center w-full px-4 md:px-0"
          >
            <p
              class="font-label font-light leading-5 text-brand text-xs md:text-sm text-center tracking-wider whitespace-nowrap"
            >
              {{ article.category }}
            </p>
            <div class="flex flex-col items-center text-center w-full gap-4">
              <h1
                class="font-serif font-normal text-[28px] md:text-[52px] text-dark text-center leading-[1.2]"
              >
                {{ article.title }}
              </h1>
              <p
                class="font-sans font-light leading-5 text-muted text-xs md:text-sm text-center"
              >
                {{ byline }}
              </p>
            </div>
            <div class="bg-primary h-px w-10 shrink-0" />
          </div>
        </div>

        <!-- INTRO / EXCERPT -->
        <div
          v-if="article.excerpt"
          class="flex flex-col items-center py-6 w-full"
        >
          <div
            class="flex flex-col gap-2.5 items-start w-full px-4 md:px-0"
          >
            <p
              class="font-serif text-center font-normal italic leading-relaxed text-dark text-md md:text-lg"
            >
              {{ article.excerpt }}
            </p>
            <div class="bg-stroke h-px w-full" />
          </div>
        </div>

        <!-- ARTICLE BODY -->
        <div class="flex items-center justify-center pt-6 w-full">
          <div
            class="journal-body font-sans font-light leading-relaxed text-dark text-sm md:text-base w-full px-4 md:px-0"
          >
            <h2 v-if="article.heading1">{{ article.heading1 }}</h2>
            <div v-if="article.paragraph1" v-html="article.paragraph1" class="prose-content" />

            <template v-if="article.image1_src">
              <img :src="article.image1_src" :alt="article.image1_caption" />
              <p class="caption">{{ article.image1_caption }}</p>
            </template>

            <h2 v-if="article.heading2">{{ article.heading2 }}</h2>
            <div v-if="article.paragraph2" v-html="article.paragraph2" class="prose-content" />

            <h2 v-if="article.heading3">{{ article.heading3 }}</h2>
            <div v-if="article.paragraph3" v-html="article.paragraph3" class="prose-content" />

            <template v-if="article.image2_src">
              <img :src="article.image2_src" :alt="article.image2_caption" />
              <p class="caption">{{ article.image2_caption }}</p>
            </template>

            <blockquote v-if="article.blockquote">
              {{ article.blockquote }}
            </blockquote>
            <div v-if="article.product_name || article.product2_name" :class="['product-block', article.product2_name ? 'product-block--multi' : '']">
              <div class="product-block-cards">
              <a :href="article.product_href" class="product-block-inner">
                <img
                  v-if="article.product_image"
                  :src="article.product_image"
                  :alt="article.product_name"
                  class="product-thumb"
                />
                <div class="product-content">
                  <div class="product-info">
                    <p class="product-label">{{ article.product_label }}</p>
                    <div class="product-name-desc">
                      <h3>{{ article.product_name }}</h3>
                      <p>{{ article.product_description }}</p>
                    </div>
                  </div>
                  <span class="product-cta">
                    View
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                      <path d="M2.5 6H9.5M9.5 6L6.5 3M9.5 6L6.5 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </span>
                </div>
              </a>
              <a v-if="article.product2_name" :href="article.product2_href" class="product-block-inner">
                <img
                  v-if="article.product2_image"
                  :src="article.product2_image"
                  :alt="article.product2_name"
                  class="product-thumb"
                />
                <div class="product-content">
                  <div class="product-info">
                    <p class="product-label">{{ article.product2_label }}</p>
                    <div class="product-name-desc">
                      <h3>{{ article.product2_name }}</h3>
                      <p>{{ article.product2_description }}</p>
                    </div>
                  </div>
                  <span class="product-cta">
                    View
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                      <path d="M2.5 6H9.5M9.5 6L6.5 3M9.5 6L6.5 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </span>
                </div>
              </a>
              </div>
            </div>

            <h2 v-if="article.heading4">{{ article.heading4 }}</h2>
            <div v-if="article.paragraph4" v-html="article.paragraph4" class="prose-content" />

            <template v-if="article.image3_src">
              <img :src="article.image3_src" :alt="article.image3_caption" />
              <p class="caption">{{ article.image3_caption }}</p>
            </template>
          </div>
        </div>

        <!-- END OF STORY -->
        <div class="flex items-center justify-center w-full mb-10 ">
          <div
            class="flex flex-col items-start p-2 w-full px-4 md:px-2"
          >
          <p
            class="font-label font-light leading-normal text-muted text-md tracking-[1px] w-full text-center"
          >
            — End of Story —
          </p>
          </div>
        </div>

        <!-- AUTHOR BIO -->
        <div
          class="flex flex-col items-start overflow-hidden py-6 w-full md:max-w-[50%] self-start"
        >
          <div
            class="flex gap-6 items-start w-full px-4 md:px-0"
          >
            <!-- Author avatar placeholder -->
            <div class="size-16 rounded-full bg-raised flex-shrink-0 overflow-hidden">
              <img
                v-if="article.author_avatar"
                :src="article.author_avatar"
                :alt="article.author_name"
                class="w-full h-full object-cover"
              />
            </div>
            <div class="flex flex-col gap-1 items-start flex-1 min-w-0">
              <p
                class="font-serif font-normal leading-normal text-dark text-lg w-full"
              >
                {{ article.author_name }}
              </p>
              <p
                v-if="article.author_bio"
                class="font-sans font-light leading-[22px] text-secondary text-[13px] w-full"
              >
                {{ article.author_bio }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- MORE STORIES -->
      <section
        v-if="moreStories.length"
        class="w-full py-12 md:py-24"
      >
        <div class="max-w-7xl mx-auto px-4">
          <header
            class="flex flex-col md:flex-row md:items-end justify-between pb-4 mb-8 border-b border-stroke"
          >
            <div class="flex flex-col gap-2">
              <p class="font-label font-light leading-4 tracking-wider text-brand">
                FROM THE MAGAZINE
              </p>
              <!-- <h2 class="font-serif font-normal text-2xl text-dark">
                More Stories
              </h2> -->
            </div>
            <NuxtLink to="/journal" class="mt-4 md:mt-0">
              <AppButton type="Ghost" size="Small" label="VIEW ALL STORIES" :showIconRight="true" />
            </NuxtLink>
          </header>

          <div class="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
            <div
              v-for="story in moreStories"
              :key="story.handle"
              class="w-[75%] sm:w-[45%] lg:w-[calc(25%-12px)] flex-shrink-0"
            >
              <AppCard
                type="Feature"
                :image="story.image"
                :categories="story.category"
                :title="story.title"
                :slug="`/journal/${story.handle}`"
              />
            </div>
          </div>
        </div>
      </section>
    </template>

  </div>
</template>

<style scoped>
.journal-body :deep(h1),
.journal-body :deep(h2),
.journal-body :deep(h3) {
  font-family: var(--font-serif, Georgia, serif);
  font-weight: 400;
  color: var(--color-text-primary, #1c1a16);
  margin-top: 2rem;
  margin-bottom: 0.75rem;
  line-height: 1.3;
}

.journal-body :deep(h2) {
  font-size: 1.5rem;
}
.journal-body :deep(h3) {
  font-size: 1.25rem;
}

.journal-body :deep(p) {
  margin-bottom: 1.25rem;
  font-size: 0.85rem; /* mobile */
  line-height: 1.75;
}

@media (min-width: 768px) {
  .journal-body :deep(p) {
    font-size: 1rem; /* desktop */
    line-height: 1.75;
  }
}

.journal-body :deep(blockquote) {
  border-left: 2px solid var(--color-interactive-primary, #c8a87a);
  padding-left: 1.25rem;
  font-style: italic;
  color: var(--color-text-secondary, #6b6050);
  margin: 1.5rem 0;
}

.journal-body .prose-content :deep(img) {
  width: 100%;
  height: auto;
  object-fit: cover;
  margin: 1.5rem 0;
}

.journal-body .prose-content :deep(a) {
  color: var(--color-text-brand, #ac9069);
  text-decoration: underline;
}

.journal-body :deep(ul),
.journal-body :deep(ol) {
  padding-left: 1.5rem;
  margin-bottom: 1.25rem;
}

.journal-body :deep(li) {
  margin-bottom: 0.5rem;
}

.journal-body h2 {
  font-family: var(--font-serif, Georgia, serif);
  font-weight: 400;
  color: var(--color-text-primary, #1c1a16);
  font-size: 1.5rem;
  margin-top: 2rem;
  margin-bottom: 0.75rem;
  line-height: 1.3;
}

.journal-body p {
  margin-bottom: 1.25rem;
}

.journal-body blockquote {
  border-left: 2px solid var(--color-interactive-primary, #c8a87a);
  padding-left: 1.25rem;
  font-style: italic;
  color: var(--color-text-secondary, #6b6050);
  margin: 1.5rem 0;
}

.journal-body img {
  width: 100%;
  height: auto;
  object-fit: cover;
  margin-top: 1.5rem;
  margin-bottom: 0.25rem;
}

.journal-body .caption {
  font-family: var(--font-sans);
  font-weight: 300;
  font-size: 0.75rem;
  line-height: 1.625;
  color: var(--color-text-secondary);
  margin-bottom: 1.5rem;
  text-align: left;
}

@media (min-width: 768px) {
  .journal-body .caption {
    font-size: 1rem;
  }
}

.journal-body .product-block {
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  margin-right: calc(-50vw + 50%);
  margin-top: 2rem;
  margin-bottom: 2rem;
  padding: 2rem 0;
  background-color: var(--color-bg-dark);
  display: flex;
  align-items: center;
  justify-content: center;
}

.journal-body .product-block-cards {
  display: flex;
  align-items: start;
  gap: 1rem;
  width: 66.67%;
}

.journal-body .product-block-inner {
  display: flex;
  gap: 1rem;
  align-items: start;
  width: 100%;
  flex: 1;
  min-width: 0;
  text-decoration: none;
  color: inherit;
}

.journal-body .product-thumb {
  width: 120px;
  height: 120px;
  flex-shrink: 1;
  object-fit: cover;
  margin: 0;
}

.journal-body .product-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
  justify-content: center;
  min-width: 0;
}

.journal-body .product-info {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.journal-body .product-label {
  font-family: var(--font-label);
  font-weight: 300;
  font-size: 9px;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: var(--color-text-brand);
  margin: 0;
}

.journal-body .product-name-desc {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  width: 100%;
}

.journal-body .product-block h3 {
  font-family: var(--font-serif);
  font-weight: 400;
  font-size: 16px;
  color: white;
  margin: 0;
  line-height: normal;
}

.journal-body .product-block p:not(.product-label) {
  font-family: var(--font-sans);
  font-weight: 300;
  font-size: 12px;
  line-height: 20px;
  color: var(--color-text-tertiary);
  margin: 0;
}

.journal-body .product-cta {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  font-family: var(--font-label);
  font-weight: 300;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.6px;
  color: var(--color-text-brand);
  text-decoration: none;
}


@media (max-width: 767px) {
  .journal-body .product-block {
    padding: 1.5rem 0;
  }

  .journal-body .product-block-cards {
    width: 100%;
    padding: 0 1rem;
  }

  .journal-body .product-block--multi .product-block-cards {
    flex-direction: column;
  }

  .journal-body .product-thumb {
    width: 96px;
    height: 96px;
  }
}
</style>
